import React, { useContext, useEffect, useRef, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { AdminWebhookDeliveryFilter, AdminWebhookDeliveryOutDTO, Pageable, WebhookDeliveryStatusEnum } from '@api/ringil-api.v2.tsx';
import AdminLayout from '@app/layout/AdminLayout.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import RingilDialog from '@components/RingilComponents/RingilDialog.tsx';
import SvgRepeatIcon from '@app/icons/admin/icon/RepeatIcon.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';

const REPLAYABLE: WebhookDeliveryStatusEnum[] = [WebhookDeliveryStatusEnum.FAILED, WebhookDeliveryStatusEnum.SENT];

/** Admin view of the outgoing webhook queue: paged + filtered, with payload preview and replay. */
const WebhookDeliveriesPage: React.FC = () => {
  const { adminWebhookDeliveryList, adminWebhookDeliveryPayload, adminWebhookDeliveryRetry } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: AdminWebhookDeliveryOutDTO[]; clear: () => void }>(null);
  const [pendingReplay, setPendingReplay] = useState<AdminWebhookDeliveryOutDTO | null>(null);
  const [replaying, setReplaying] = useState(false);
  const [payloadOf, setPayloadOf] = useState<AdminWebhookDeliveryOutDTO | null>(null);
  const [payload, setPayload] = useState<string | null>(null);

  // Cross-company view, no push channel - plain slow poll like the jobs monitoring page.
  useEffect(() => {
    const id = window.setInterval(() => tableRef.current?.reloadData(), 30000);
    return () => window.clearInterval(id);
  }, []);

  const loadDataApi = (filter: AdminWebhookDeliveryFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<AdminWebhookDeliveryOutDTO>) => void) => {
    adminWebhookDeliveryList(filter, pageable, data => {
      onSuccess({ data: data.data, total: data.total, page: data.page, size: data.size });
    });
  };

  const runReplay = (row: AdminWebhookDeliveryOutDTO) => {
    setReplaying(true);
    adminWebhookDeliveryRetry(
      row.id,
      () => {
        setReplaying(false);
        setPendingReplay(null);
        tableRef.current?.reloadData();
      },
      null,
      { onError: () => setReplaying(false) },
    );
  };

  const openPayload = (row: AdminWebhookDeliveryOutDTO) => {
    setPayloadOf(row);
    setPayload(null);
    adminWebhookDeliveryPayload(row.id, data => setPayload(data.payloadJson), null, { successToast: false });
  };

  const formatJson = (json: string): string => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  };

  const statusLabel = (status: WebhookDeliveryStatusEnum) => tr(`AdminWebhookDeliveries.status.${status}`, status);

  const renderStatusBadge = (status: WebhookDeliveryStatusEnum) => {
    switch (status) {
      case WebhookDeliveryStatusEnum.SENT:
        return <COBadge variant={'filled'} type={'positive'}>{statusLabel(status)}</COBadge>;
      case WebhookDeliveryStatusEnum.FAILED:
        return (
          <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-errorMedium px-2 py-1'}>
            {statusLabel(status)}
          </COBadge>
        );
      case WebhookDeliveryStatusEnum.IN_PROGRESS:
        return <COBadge variant={'outlined'} type={'processing'}>{statusLabel(status)}</COBadge>;
      default:
        return <COBadge variant={'outlined'} type={'info'}>{statusLabel(status)}</COBadge>;
    }
  };

  const renderWebhookColumn = (row: AdminWebhookDeliveryOutDTO) => (
    <div className={'flex flex-col gap-1'}>
      <span className={'text-content-primary'}>{row.webhookType}</span>
      {row.action != null && <span className={'text-sm text-content-secondary'}>{row.action}</span>}
      <span className={'text-sm text-content-tertiary'}>{row.id}</span>
    </div>
  );

  const renderCompanyColumn = (row: AdminWebhookDeliveryOutDTO) => (
    <div className={'flex flex-col gap-1'}>
      <span className={'text-content-primary'}>{row.companyName ?? row.companyId}</span>
      <span className={'text-sm text-content-tertiary'}>
        {row.entityType} {row.entityId}
      </span>
    </div>
  );

  const renderStateColumn = (row: AdminWebhookDeliveryOutDTO) => {
    const status = row.webhookDeliveryStatus;
    return (
      <div className={'flex flex-col gap-1'}>
        {renderStatusBadge(status)}
        <span className={'text-sm text-content-secondary'}>
          {tr('AdminWebhookDeliveries.attempts', 'Attempts')}: {row.attemptCount}/{row.maxAttempts}
          {row.lastStatusCode != null && `, HTTP ${row.lastStatusCode}`}
        </span>
        {status === WebhookDeliveryStatusEnum.PENDING && row.attemptCount > 0 && (
          <span className={'text-sm text-content-secondary'}>
            {tr('AdminWebhookDeliveries.nextAttempt', 'Next attempt')}: {stringDateTimeWithLocaleFromApiStringInTz(row.nextAttemptAt, 'Europe/Prague')}
          </span>
        )}
        {row.lastError != null && (
          <span className={'text-sm text-complementary-error break-all'} title={row.lastError}>
            {row.lastError.length > 160 ? `${row.lastError.substring(0, 160)}…` : row.lastError}
          </span>
        )}
      </div>
    );
  };

  const renderDatesColumn = (row: AdminWebhookDeliveryOutDTO) => (
    <div className={'flex flex-col text-sm text-content-secondary'}>
      <span>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, 'Europe/Prague')}</span>
      {row.finishedAt != null && <span>{stringDateTimeWithLocaleFromApiStringInTz(row.finishedAt, 'Europe/Prague')}</span>}
      {row.lockedBy != null && <span className={'text-content-tertiary'}>{row.lockedBy}</span>}
    </div>
  );

  const renderActionsColumn = (row: AdminWebhookDeliveryOutDTO) => (
    <div className={'flex flex-row justify-end gap-2'}>
      <RingilButton mode={'outlined'} label={tr('AdminWebhookDeliveries.showPayload', 'Payload')} onClick={() => openPayload(row)} />
      {REPLAYABLE.includes(row.webhookDeliveryStatus) && (
        <RingilButton mode={'outlined'} label={tr('AdminWebhookDeliveries.sendAgain', 'Send again')} onClick={() => setPendingReplay(row)} />
      )}
    </div>
  );

  const columnConfig: ColumnConfig<AdminWebhookDeliveryOutDTO>[] = [
    { id: 'webhook', header: 'AdminWebhookDeliveries.columnWebhook', headerDefault: 'Webhook', width: '22%', order: 1, render: renderWebhookColumn },
    { id: 'company', header: 'AdminWebhookDeliveries.columnCompany', headerDefault: 'Company / entity', width: '23%', order: 2, render: renderCompanyColumn },
    { id: 'state', header: 'AdminWebhookDeliveries.columnState', headerDefault: 'State', width: '25%', order: 3, render: renderStateColumn },
    { id: 'dates', header: 'AdminWebhookDeliveries.columnDates', headerDefault: 'Created / finished', width: '15%', order: 4, render: renderDatesColumn },
    { id: 'actions', header: '', headerDefault: '', width: '15%', order: 5, render: renderActionsColumn },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('AdminWebhookDeliveries.filterStatus', 'Status'),
      label: tr('AdminWebhookDeliveries.filterStatus', 'Status'),
      value: ['webhookDeliveryStatus'],
      options: Object.values(WebhookDeliveryStatusEnum).map(status => ({ label: statusLabel(status), value: status, active: true })),
      dataTestId: 'admin-webhook-deliveries-filter-status',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('AdminWebhookDeliveries.filterCompany', 'Company'),
      label: tr('AdminWebhookDeliveries.filterCompany', 'Company'),
      value: ['companyName'],
      dataTestId: 'admin-webhook-deliveries-filter-company',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('AdminWebhookDeliveries.filterWebhookType', 'Webhook type'),
      label: tr('AdminWebhookDeliveries.filterWebhookType', 'Webhook type'),
      value: ['webhookType'],
      dataTestId: 'admin-webhook-deliveries-filter-type',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('AdminWebhookDeliveries.filterEntityId', 'Entity ID'),
      label: tr('AdminWebhookDeliveries.filterEntityId', 'Entity ID'),
      value: ['entityId'],
      dataTestId: 'admin-webhook-deliveries-filter-entity',
    },
  ];

  return (
    <AdminLayout title={'Webhook deliveries'}>
      <BreadCrumbComponent items={[{ label: 'Webhook deliveries' }]} />
      <div className={'flex flex-row items-center py-4'}>
        <COHeadline title={'Webhook deliveries'} variant={'h2'} icon={<SvgRepeatIcon />} />
      </div>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'admin_webhook_deliveries'}
        enableReload
      />
      <ConfirmDialog
        visible={!!pendingReplay}
        loading={replaying}
        dialogTitle={tr('AdminWebhookDeliveries.sendAgainConfirmTitle', 'Send again')}
        dialogMessage={tr(
          'AdminWebhookDeliveries.sendAgainConfirmMessage',
          'The webhook will be queued again with a fresh attempt budget. The stored payload is sent as it was captured.',
        )}
        onConfirm={() => pendingReplay && runReplay(pendingReplay)}
        onCancel={() => setPendingReplay(null)}
        onAcceptButtonText={tr('AdminWebhookDeliveries.sendAgain', 'Send again')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
      />
      <RingilDialog
        visible={payloadOf != null}
        heading={`${tr('AdminWebhookDeliveries.showPayload', 'Payload')} – ${payloadOf?.webhookType ?? ''}`}
        onHide={() => setPayloadOf(null)}
        size={'lg'}
      >
        <div className={'border rounded p-4 bg-gray-50 overflow-auto max-h-[600px]'}>
          <pre className={'text-sm whitespace-pre-wrap break-words'}>{payload != null ? formatJson(payload) : '…'}</pre>
        </div>
      </RingilDialog>
    </AdminLayout>
  );
};

export default withAuthenticationRequired(WebhookDeliveriesPage);
